import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Xmark, Bug, LightBulb, Heart, Send, ChatBubble, Star } from "iconoir-react";
import { submitFeedback } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

export default function FeedbackDialog({ open, onClose }) {
    const { t } = useT();
    const [rating, setRating] = useState(0);
    const [bug, setBug] = useState("");
    const [feature, setFeature] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const reset = () => {
        setRating(0);
        setBug("");
        setFeature("");
        setNotes("");
    };

    const close = () => {
        onClose?.();
        setTimeout(reset, 300);
    };

    const send = async () => {
        if (!rating && !bug.trim() && !feature.trim() && !notes.trim()) {
            toast.error(t("feedback.empty"));
            return;
        }
        setSubmitting(true);
        try {
            await submitFeedback({
                rating: rating || null,
                bug,
                feature,
                notes,
                page: window.location.pathname,
            });
            toast.success(t("feedback.sent"));
            close();
        } catch {
            toast.error(t("feedback.send_failed"));
        } finally {
            setSubmitting(false);
        }
    };

    const ratingLabels = ["", t("feedback.rating1"), t("feedback.rating2"), t("feedback.rating3"), t("feedback.rating4"), t("feedback.rating5")];

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-[#0D2A1B]/40 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-4 pt-10 overflow-y-auto"
                    onClick={close}
                    data-testid="feedback-dialog"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96 }}
                        onClick={(e) => e.stopPropagation()}
                        className="brut-card p-6 sm:p-7 w-full max-w-md my-auto"
                    >
                        <div className="flex items-start justify-between mb-4">
                            <div className="font-heading text-2xl font-black tracking-tight">
                                {t("feedback.headline")}
                            </div>
                            <button
                                data-testid="close-feedback-btn"
                                onClick={close}
                                className="w-9 h-9 brut-border rounded-lg bg-white flex items-center justify-center hover:bg-[#FBF6EB]"
                                aria-label="close"
                            >
                                <Xmark className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="brut-border rounded-xl bg-[#CFE4D0] p-3 flex gap-2 mb-5 text-sm">
                            <ChatBubble className="w-4 h-4 shrink-0 mt-0.5" />
                            <div>
                                {t("feedback.intro")}
                            </div>
                        </div>

                        <div className="mb-5">
                            <div className="font-heading font-black mb-2">
                                {t("feedback.rating_q")}
                            </div>
                            <div className="flex gap-2 justify-between">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                        key={n}
                                        data-testid={`star-${n}-btn`}
                                        onClick={() => setRating(n)}
                                        aria-label={`${n} star`}
                                        className={`w-12 h-12 brut-border rounded-xl flex items-center justify-center transition-all ${
                                            rating >= n
                                                ? "bg-[#F3D469] brut-shadow-sm"
                                                : "bg-white hover:bg-[#FBF6EB]"
                                        }`}
                                    >
                                        <Star
                                            className={`w-5 h-5 ${
                                                rating >= n ? "" : "opacity-40"
                                            }`}
                                            fill={rating >= n ? "#0D2A1B" : "none"}
                                        />
                                    </button>
                                ))}
                            </div>
                            <div className="text-xs text-center mt-2 text-[#255636] font-medium">
                                {rating === 0 ? t("feedback.tap_star") : ratingLabels[rating]}
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="font-heading font-black text-lg flex items-center gap-2 mb-1.5">
                                    <Bug className="w-5 h-5 text-[#D7533C]" /> {t("feedback.bug_q")}
                                </label>
                                <textarea
                                    data-testid="feedback-bug-input"
                                    value={bug}
                                    onChange={(e) => setBug(e.target.value)}
                                    rows={2}
                                    placeholder={t("feedback.bug_placeholder")}
                                    className="brut-input resize-none text-sm"
                                />
                            </div>
                            <div>
                                <label className="font-heading font-black text-lg flex items-center gap-2 mb-1.5">
                                    <LightBulb className="w-5 h-5 text-[#E9B872]" /> {t("feedback.feature_q")}
                                </label>
                                <textarea
                                    data-testid="feedback-feature-input"
                                    value={feature}
                                    onChange={(e) => setFeature(e.target.value)}
                                    rows={2}
                                    placeholder={t("feedback.feature_placeholder")}
                                    className="brut-input resize-none text-sm"
                                />
                            </div>
                            <div>
                                <label className="font-heading font-black text-lg flex items-center gap-2 mb-1.5">
                                    <Heart className="w-5 h-5 text-[#E05B8B]" /> {t("feedback.notes_q")}
                                </label>
                                <textarea
                                    data-testid="feedback-notes-input"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={2}
                                    placeholder={t("feedback.notes_placeholder")}
                                    className="brut-input resize-none text-sm"
                                />
                            </div>
                        </div>

                        <button
                            data-testid="submit-feedback-btn"
                            onClick={send}
                            disabled={submitting}
                            className="brut-btn w-full justify-center mt-5 text-base"
                        >
                            <Send className="w-4 h-4" />
                            {submitting ? t("feedback.sending") : t("feedback.send_btn")}
                        </button>

                        <div className="text-xs text-[#255636] text-center font-medium mt-3">
                            {t("feedback.footer")}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
